import { useState } from "react";
import { Aviso, Botao, Campo, CampoDeTelefone } from "../ui";
import { mensagemDoConsole } from "../../nucleo/console";
import type { MatrizDeTelas } from "../../nucleo/telas";
import { useConvidarUsuario } from "../../nucleo/usuarios";
import { useVoltarParaHome } from "../../nucleo/voltarParaHome";
import { telefoneCompleto } from "../../nucleo/validacao";
import { TelasDoPerfil } from "./TelasDoPerfil";

/**
 * Convite de uma pessoa — nome, e-mail, cargo, telefone e perfil. É o mesmo formulário para o
 * administrador do cliente e para a equipe interna; o que muda é a lista de perfis oferecida e,
 * no servidor, a organização (que sempre vem da sessão, nunca daqui).
 */
export function ConviteDePessoa({
  perfis,
  perfilInicial,
  matriz,
}: {
  perfis: readonly { valor: string; rotulo: string }[];
  perfilInicial: string;
  matriz: MatrizDeTelas | undefined;
}) {
  const convidar = useConvidarUsuario();
  const voltarParaHome = useVoltarParaHome();
  const [nome, setNome] = useState("");
  const [email, setEmail] = useState("");
  const [cargo, setCargo] = useState("");
  const [telefone, setTelefone] = useState("");
  const [papel, setPapel] = useState(perfilInicial);

  function enviar() {
    convidar.mutate(
      {
        nome: nome.trim(),
        email: email.trim(),
        cargo: cargo.trim() || null,
        telefone: telefone.trim() || null,
        papel_chave: papel,
      },
      {
        onSuccess: (convite) => {
          setNome("");
          setEmail("");
          setCargo("");
          setTelefone("");
          // O aviso (inclusive o do modo de teste) viaja até a home do lado de quem convidou,
          // onde aparece uma vez.
          voltarParaHome(
            convite.modo_teste
              ? `Convite preparado para ${convite.email} em modo de teste; o e-mail não foi enviado ao destinatário.`
              : `Convite enviado para ${convite.email}.`,
          );
        },
      },
    );
  }

  return (
    <section className="mb-6" aria-labelledby="titulo-convidar-usuario">
      <h2 className="titulo-secao" id="titulo-convidar-usuario">Convidar uma pessoa</h2>
      <div className="grid gap-3 md:grid-cols-2">
        <Campo
          rotulo="Nome"
          value={nome}
          onChange={(evento) => setNome(evento.target.value)}
          obrigatorio
          data-testid="novo-usuario-nome"
        />
        <Campo
          rotulo="E-mail"
          type="email"
          value={email}
          onChange={(evento) => setEmail(evento.target.value)}
          obrigatorio
          data-testid="novo-usuario-email"
        />
        <Campo
          rotulo="Cargo (opcional)"
          value={cargo}
          onChange={(evento) => setCargo(evento.target.value)}
          data-testid="novo-usuario-cargo"
        />
        <CampoDeTelefone
          rotulo={papel === "key_user" ? "WhatsApp" : "Telefone (opcional)"}
          obrigatorio={papel === "key_user"}
          valor={telefone}
          aoMudar={setTelefone}
          data-testid="novo-usuario-telefone"
        />
      </div>
      <label className="campo__rotulo" htmlFor="perfil-do-convite">
        Perfil
      </label>
      <select
        id="perfil-do-convite"
        className="campo__controle mb-3"
        value={papel}
        onChange={(evento) => setPapel(evento.target.value)}
        data-testid="novo-usuario-perfil"
      >
        {perfilInicial === "" ? <option value="">Escolha um perfil</option> : null}
        {perfis.map((perfil) => (
          <option key={perfil.valor} value={perfil.valor}>
            {perfil.rotulo}
          </option>
        ))}
      </select>
      <TelasDoPerfil matriz={matriz} perfil={papel} />
      {convidar.isError ? (
        <Aviso tom="erro" className="mb-3">
          {mensagemDoConsole(convidar.error)}
        </Aviso>
      ) : null}
      <Botao
        onClick={enviar}
        disabled={!nome.trim() || !email.trim() || !papel || (papel === "key_user" && !telefoneCompleto(telefone)) || convidar.isPending}
        data-testid="convidar-usuario"
      >
        {convidar.isPending ? "Enviando…" : "Enviar convite"}
      </Botao>
    </section>
  );
}
